import React, { useState, useEffect } from 'react';
import {
	BringzzButton, BringzzText, BringzzInput, BringzzPageContainer,
	BringzzIcon
} from "@bringzz/components";
import { useNavigation } from 'context/NavigationContext';
import { useAuth } from 'context/AuthContext';
import useRequest from 'hooks/useRequest';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import '../auth.css';

const LoginPage = () => {
	const { navigate, backPress } = useNavigation();
	const { login, loginWithToken } = useAuth();
	const { sendRequest } = useRequest();
	const { t } = useTranslation();

	const [country, setCountry] = useState({
		name: 'Austria',
		dial_code: '+43',
		code: 'AT'
	});

	const [identifierValue, setIdentifierValue] = useState('');
	const [identifierDisplay, setIdentifierDisplay] = useState('');
	const [password, setPassword] = useState('');
	const [show, setShow] = useState(false);
	const [localLoading, setLocalLoading] = useState(false);
	const [loginFailed, setLoginFailed] = useState(false);
	const [passwordTouched, setPasswordTouched] = useState(false);
	const [passwordFocused, setPasswordFocused] = useState(false);
	const [isSSOAuthProcessing, setIsSSOAuthProcessing] = useState(false);

	const phoneRegex = /^\d{5,}$/;
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	// Prefill identifier from query params (coming from /auth)
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const method = urlParams.get('method');
		const value = urlParams.get('value');

		if (value) {
			const decoded = decodeURIComponent(value);
			if (method === 'phone') {
				// Value comes as "+43 123456" — extract dial code and number
				const parts = decoded.split(' ');
				if (parts.length >= 2) {
					const dialCode = parts[0];
					const number = parts.slice(1).join('');
					setCountry(prev => ({ ...prev, dial_code: dialCode }));
					setIdentifierValue(number);
					setIdentifierDisplay(decoded);
				}
			} else {
				setIdentifierValue(decoded);
				setIdentifierDisplay(decoded);
			}
		}
	}, []);

	const getMethod = () => {
		if (phoneRegex.test(identifierValue)) return 'phone';
		if (emailRegex.test(identifierValue)) return 'email';
		return null;
	};

	const getLoginValue = () => {
		const method = getMethod();
		if (method === 'phone') return `${country.dial_code} ${identifierValue}`;
		return identifierValue;
	};

	const isFormValid = identifierValue && password.length >= 9;

	// SSO callback handling (for OAuth redirects that land on /login)
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const code = urlParams.get('code');
		const state = urlParams.get('state');

		if (code && state) {
			if (state.startsWith('google#')) {
				handleGoogleCallback(code, state);
			} else if (state.startsWith('facebook#')) {
				handleFacebookCallback(code, state);
			}
		}
	}, []);

	const handleGoogleCallback = async (code, state) => {
		setIsSSOAuthProcessing(true);
		try {
			const response = await sendRequest({
				method: 'POST',
				url: `/module/user/googleAuthCallback`,
				data: { code, state }
			});

			if (response.data) {
				if (response.message === "signupRequired") {
					const userData = response.data;
					const preUserResponse = await sendRequest({
						method: 'POST',
						url: `/module/user/preUserCreation`,
						data: {
							login: userData.email,
							password: "",
							logintype: 2,
							language: "en"
						}
					});

					if (preUserResponse.status === 'success' && preUserResponse.data.preuser) {
						const userId = preUserResponse.data.preuser;
						const email = encodeURIComponent(userData.email);
						let profileUrl = `/register/consumer/profile?method=email&userId=${userId}&value=${email}`;
						if (userData.givenName) profileUrl += `&firstName=${encodeURIComponent(userData.givenName)}`;
						if (userData.familyName) profileUrl += `&lastName=${encodeURIComponent(userData.familyName)}`;
						if (userData.picture) profileUrl += `&profilePicture=${encodeURIComponent(userData.picture)}`;
						navigate(profileUrl);
					}
				} else {
					loginWithToken(response.data.token, response.data);
					toast.success(t('component.auth.success.google_login'));
				}
			} else {
				toast.info(t('component.auth.errors.google_login_failed'));
				setIsSSOAuthProcessing(false);
			}
		} catch (error) {
			console.error('Error during Google login callback', error);
			toast.error(t('component.auth.errors.google_login_failed'));
			setIsSSOAuthProcessing(false);
		}
	};

	const handleFacebookCallback = async (code, state) => {
		setIsSSOAuthProcessing(true);
		try {
			const response = await sendRequest({
				method: 'POST',
				url: `/module/user/facebookAuthCallback`,
				data: { code, state }
			});

			if (response.data) {
				if (response.message === "signupRequired") {
					const userData = response.data;
					const preUserResponse = await sendRequest({
						method: 'POST',
						url: `/module/user/preUserCreation`,
						data: {
							login: userData.email,
							password: "",
							logintype: 3,
							language: "en"
						}
					});

					if (preUserResponse.status === 'success' && preUserResponse.data.preuser) {
						const userId = preUserResponse.data.preuser;
						const email = encodeURIComponent(userData.email);
						let profileUrl = `/register/consumer/profile?method=email&userId=${userId}&value=${email}`;
						if (userData.givenName) profileUrl += `&firstName=${encodeURIComponent(userData.givenName)}`;
						if (userData.familyName) profileUrl += `&lastName=${encodeURIComponent(userData.familyName)}`;
						if (userData.picture) profileUrl += `&profilePicture=${encodeURIComponent(userData.picture)}`;
						navigate(profileUrl);
					}
				} else {
					loginWithToken(response.data.token, response.data);
					toast.success(t('component.auth.success.facebook_login'));
				}
			} else {
				toast.info(t('component.auth.errors.facebook_login_failed'));
				setIsSSOAuthProcessing(false);
			}
		} catch (error) {
			console.error('Error during Facebook login callback', error);
			toast.error(t('component.auth.errors.facebook_login_failed'));
			setIsSSOAuthProcessing(false);
		}
	};

	const handleLogin = async () => {
		const credentials = {
			username: getLoginValue(),
			password: password
		};
		setLocalLoading(true);
		try {
			const success = await login(credentials);
			setLocalLoading(false);
			if (success) {
				navigate('/home');
			} else {
				setLoginFailed(true);
			}
		} catch (error) {
			setLocalLoading(false);
			setLoginFailed(true);
		}
	};

	const handleForgotPassword = async () => {
		const method = getMethod();
		if (!method) {
			toast.info(t('component.auth.login.forgot_password_enter_email'));
			return;
		}

		setLocalLoading(true);
		try {
			const loginValue = getLoginValue();
			const response = await sendRequest({
				method: 'POST',
				url: '/module/token/requestPasswordReset',
				data: { login: loginValue, method, language: 'en' }
			});

			setLocalLoading(false);

			if (response.status === 'success' && response.data?.userId) {
				const encodedValue = encodeURIComponent(loginValue);
				navigate(`/login/otp?method=${method}&value=${encodedValue}&userId=${response.data.userId}&redirect=/reset-password`);
			} else {
				toast.info(t('component.auth.login.forgot_password_generic'));
			}
		} catch (error) {
			setLocalLoading(false);
			toast.error(t('component.auth.login.errors.generic_error'));
		}
	};

	const getPasswordValidationState = () => {
		if (passwordTouched && !passwordFocused) {
			if (password === '') return 'error';
			if (loginFailed) return 'error';
		}
		return null;
	};

	const getPasswordMessage = () => {
		if (passwordTouched && !passwordFocused) {
			if (password === '') return t('component.auth.register.password.required');
			if (loginFailed) return t('component.auth.login.errors.login_failed');
		}
		return null;
	};

	if (isSSOAuthProcessing) {
		return (
			<BringzzPageContainer className='bg-magic-lilac auth-no-scroll' authenticated={false}>
				<div className='flex justify-center items-center h-full'>
					<BringzzText className='text-midnight-blue font-medium text-lg'>
						{"Processing ... Please wait."}
					</BringzzText>
				</div>
			</BringzzPageContainer>
		);
	}

	return (
		<BringzzPageContainer
			className='bg-magic-lilac auth-no-scroll'
			authenticated={false}
			header={{
				left: (
					<div className='flex items-center gap-x-1 cursor-pointer' onClick={backPress}>
						<BringzzIcon
							size='22'
							icon='IconChevronLeft'
							folder='LineIcons'
						/>
						<BringzzText tag='h4' className='font-medium text-midnight-blue'>
							{t('component.auth.login.back')}
						</BringzzText>
					</div>
				)
			}}
			footer={
				<div className='pb-20 px-6'>
					<BringzzButton
						size='lg'
						className='bg-midnight-blue w-full flex justify-center text-white'
						onClick={handleLogin}
						disabled={!isFormValid || localLoading}
					>
						{t('component.auth.buttons.sign_in')}
					</BringzzButton>
					<div className='flex items-center gap-x-4 my-4'>
						<div className='grow bg-sand h-[1px]'></div>
						<BringzzText tag='h4' className='!text-sand font-normal whitespace-nowrap'>
							{t('component.auth.login.buttons.new_user')}
						</BringzzText>
						<div className='grow bg-sand h-[1px]'></div>
					</div>
					<BringzzButton
						size='lg'
						className='bg-white border border-midnight-blue w-full flex justify-center text-midnight-blue'
						onClick={() => navigate('/register')}
					>
						{t('component.auth.login.buttons.create_account')}
					</BringzzButton>
				</div>
			}
		>
			<div className='flex flex-col h-full w-full overflow-hidden'>
				<div className='flex flex-col px-4 pt-8'>
					<BringzzText tag='h1' className='font-bold text-midnight-blue mb-6'>
						{t('component.auth.login.enter_password')}
					</BringzzText>
					<form
						className='flex flex-col'
						onSubmit={(e) => {
							e.preventDefault();
							if (isFormValid && !localLoading) {
								handleLogin();
							}
						}}
					>
						<BringzzInput
							type='text'
							value={identifierDisplay}
							label={getMethod() === 'phone' ? t('component.auth.login.method_labels.phone') : t('component.auth.login.method_labels.email')}
							inputProps={{
								readOnly: true
							}}
							icon={{
								right: (
									<BringzzText
										tag='h4'
										className='text-midnight-blue underline cursor-pointer font-medium whitespace-nowrap'
										onClick={backPress}
									>
										{t('component.auth.login.buttons.edit')}
									</BringzzText>
								)
							}}
						/>
						<div className='mt-4'>
							<BringzzInput
								icon={{
									right: (
										<BringzzIcon
											icon={show ? 'IconEye' : 'IconEyeSlash'}
											folder='LineIcons'
											className='cursor-pointer'
											size='16'
											onClick={() => setShow(!show)}
										/>
									)
								}}
								type={show ? "text" : "password"}
								label={t('component.auth.login.password.label')}
								pattern="^.{9,}$"
								validationState={getPasswordValidationState()}
								customMessage={getPasswordMessage()}
								onChange={(e, isValid, newValue) => {
									setPassword(newValue);
									setPasswordTouched(true);
									setLoginFailed(false);
								}}
								onBlur={() => {
									setPasswordFocused(false);
									setPasswordTouched(true);
								}}
								inputProps={{
									onFocus: () => setPasswordFocused(true)
								}}
								onKeyDown={(e) => {
									if ((e.key === 'Enter' || e.key === 'Return' || e.keyCode === 13) && isFormValid && !localLoading) {
										e.preventDefault();
										handleLogin();
									}
								}}
							/>
						</div>
						<div className='flex items-center justify-start mt-2'>
							<BringzzText
								tag='h4'
								className={`text-midnight-blue underline font-medium ${localLoading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
								onClick={localLoading ? undefined : handleForgotPassword}
							>
								{t('component.auth.login.password.forgot')}
							</BringzzText>
						</div>
						<button type="submit" style={{ display: 'none' }} />
					</form>
				</div>
			</div>
		</BringzzPageContainer>
	);
};

export default LoginPage;
